<template>
  <div class="question-bank-list">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="page-icon">
            <i class="bi bi-collection"></i>
          </div>
          <div class="header-text">
            <h1 class="page-title">题库管理</h1>
            <p class="page-subtitle">管理和维护系统中的所有题库</p>
          </div>
        </div>
        <div class="header-actions">
          <router-link to="/admin/question-banks/import" class="btn btn-outline-primary me-2">
            <i class="bi bi-cloud-upload me-1"></i> 导入题库
          </router-link>
          <router-link to="/admin/question-banks/import/case-study" class="btn btn-outline-info me-2">
            <i class="bi bi-images me-1"></i> 导入案例题
          </router-link>
          <router-link to="/admin/question-banks/new" class="btn btn-primary">
            <i class="bi bi-plus-lg me-1"></i> 新建题库
          </router-link>
        </div>
      </div>
    </div>
    <!-- 搜索栏 -->
    <div class="search-section">
      <div class="row">
        <div class="col-md-6">
          <div class="input-group">
            <span class="input-group-text">
              <i class="bi bi-search"></i>
            </span>
            <input
              type="text"
              class="form-control"
              placeholder="搜索题库名称或描述..."
              v-model="searchQuery"
              @input="handleSearch"
              data-test="search-input"
            />
          </div>
        </div>
        <div class="col-md-3">
          <select class="form-select" v-model="selectedDomain" @change="handleFilter">
            <option value="">所有领域</option>
            <option v-for="domain in domains" :key="String(domain)" :value="domain">
              {{ domain }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <select class="form-select" v-model="selectedStatus" @change="handleFilter">
            <option value="">所有状态</option>
            <option value="active">启用</option>
            <option value="inactive">禁用</option>
          </select>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">加载中...</span>
      </div>
      <p class="mt-3 text-muted">正在加载题库数据...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <i class="bi bi-exclamation-triangle me-2"></i>
        {{ error }}
      </div>
      <button class="btn btn-outline-primary" @click="loadQuestionBanks">
        <i class="bi bi-arrow-clockwise me-1"></i>
        重试
      </button>
    </div>

    <!-- 空状态 -->
    <div v-else-if="questionBanks.length === 0" class="text-center py-5">
      <i class="bi bi-journal-x" style="font-size: 4rem; color: #ccc;"></i>
      <h5 class="mt-3 text-muted">暂无题库数据</h5>
      <p class="text-muted">您可以创建新的题库或导入现有题库</p>
      <router-link to="/admin/question-banks/new" class="btn btn-primary">
        <i class="bi bi-plus-lg me-1"></i>
        创建第一个题库
      </router-link>
    </div>

    <!-- 题库列表 -->
    <div v-else>
      <div class="row">
        <div class="col-md-6 col-lg-4 mb-4" v-for="bank in filteredQuestionBanks" :key="bank.id">
          <div class="card question-bank-card h-100">
            <div class="card-header">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h5 class="card-title mb-1">{{ bank.description || bank.subdomain }}</h5>
                  <small class="text-muted">{{ bank.domain }}</small>
                </div>
                <span class="badge" :class="bank.isActive ? 'bg-success' : 'bg-secondary'">
                  {{ bank.isActive ? '启用' : '禁用' }}
                </span>
              </div>
            </div>
            
            <div class="card-body">
              <div class="row text-center">
                <div class="col-6">
                  <div class="stat-item">
                    <div class="stat-number">{{ getQuestionCount(bank) }}</div>
                    <div class="stat-label">题目</div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="stat-item">
                    <div class="stat-number">{{ formatDate(bank.createdAt) }}</div>
                    <div class="stat-label">创建时间</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="card-footer">
              <div class="d-flex justify-content-between align-items-center">
                <router-link 
                  :to="`/admin/question-banks/${bank.id}`" 
                  class="btn btn-outline-primary btn-sm"
                >
                  <i class="bi bi-eye me-1"></i>
                  查看
                </router-link>
                <div class="action-buttons">
                  <router-link 
                    :to="`/admin/question-banks/${bank.id}/edit`" 
                    class="btn btn-sm btn-warning me-2"
                    title="编辑题库"
                  >
                    <i class="bi bi-pencil"></i>
                    <span class="d-none d-sm-inline ms-1">编辑</span>
                  </router-link>
                  <button 
                    class="btn btn-sm btn-danger" 
                    @click="confirmDelete(bank)"
                    data-test="delete-btn"
                    title="删除题库"
                  >
                    <i class="bi bi-trash"></i>
                    <span class="d-none d-sm-inline ms-1">删除</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 统计信息 -->
      <div class="d-flex justify-content-between align-items-center mt-4">
        <div class="text-muted">
          共 {{ filteredQuestionBanks.length }} 个题库
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { questionBanksApi } from '../../api/questionBanks'
import type { QuestionBank } from '../../types'
import { useAuthStore } from '../../stores/auth'

const router = useRouter()
const toast = useToast()
const authStore = useAuthStore()

// 状态
const questionBanks = ref<QuestionBank[]>([])
const loading = ref(false)
const error = ref('')
const searchQuery = ref('')
const selectedDomain = ref('')
const selectedStatus = ref('')

// 计算属性
const isAdmin = computed(() => authStore.isAdmin)

const filteredQuestionBanks = computed(() => {
  return questionBanks.value.filter((bank: QuestionBank) => {
    const matchesSearch = !searchQuery.value || 
      bank.subdomain.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      (bank.description && bank.description.toLowerCase().includes(searchQuery.value.toLowerCase()))
    
    const matchesDomain = !selectedDomain.value || bank.domain === selectedDomain.value
    const matchesStatus = !selectedStatus.value || 
      (selectedStatus.value === 'active' && bank.isActive) ||
      (selectedStatus.value === 'inactive' && !bank.isActive)
    
    return matchesSearch && matchesDomain && matchesStatus
  })
})

const domains = computed(() => {
  const uniqueDomains = new Set(questionBanks.value.map((bank: QuestionBank) => bank.domain))
  return Array.from(uniqueDomains)
})

const getStatusBadgeClass = (bank: QuestionBank) => {
  return bank.isActive ? 'bg-success' : 'bg-secondary'
}

const getStatusText = (bank: QuestionBank) => {
  return bank.isActive ? '启用' : '禁用'
}

const getQuestionCount = (bank: QuestionBank) => {
  return bank.totalQuestions || 0
}

// 方法
const loadQuestionBanks = async () => {
  loading.value = true
  error.value = ''
  
  try {
    const response = await questionBanksApi.getQuestionBanks()
    questionBanks.value = response.data || []
  } catch (err: any) {
    error.value = err.response?.data?.message || '加载题库失败'
    toast.error(error.value)
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  // 搜索逻辑已通过计算属性实现
}

const handleFilter = () => {
  // 过滤逻辑已通过计算属性实现
}

const confirmDelete = async (bank: QuestionBank) => {
  if (confirm(`确定要删除题库 "${bank.description || bank.subdomain}" 吗？此操作不可恢复。`)) {
    try {
      await questionBanksApi.deleteQuestionBank(bank.id)
      toast.success('题库删除成功')
      loadQuestionBanks()
    } catch (err: any) {
      const message = err.response?.data?.message || '删除题库失败'
      toast.error(message)
    }
  }
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('zh-CN')
}

// 生命周期
onMounted(() => {
  loadQuestionBanks()
})
</script>

<style scoped>
/* 现代化页面样式 */
.question-bank-list {
  padding: 2rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

.page-header {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7));
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
}

.page-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.5rem;
  font-size: 1.5rem;
  color: white;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
}

.header-text {
  flex: 1;
}

.page-title {
  margin: 0;
  color: #2c3e50;
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #2c3e50, #3498db);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-subtitle {
  margin: 0.5rem 0 0 0;
  color: #6c757d;
  font-size: 1rem;
  font-weight: 400;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
  border: none;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn:hover::before {
  left: 100%;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.btn-outline-primary {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border: 2px solid rgba(102, 126, 234, 0.3);
}

.btn-outline-primary:hover {
  background: rgba(102, 126, 234, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

.search-section {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.form-control, .form-select {
  border-radius: 12px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
}

.form-control:focus, .form-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
  background: white;
}

.input-group-text {
  background: rgba(102, 126, 234, 0.1);
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-right: none;
  color: #667eea;
  border-radius: 12px 0 0 12px;
}

.input-group .form-control {
  border-left: none;
  border-radius: 0 12px 12px 0;
}

.question-bank-card {
  transition: all 0.3s ease;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.question-bank-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.card-header {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
}

.card-title {
  font-weight: 600;
  color: #2c3e50;
}

.badge {
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-weight: 500;
}

.bg-success {
  background: linear-gradient(135deg, #00b894, #00cec9) !important;
}

.bg-secondary {
  background: linear-gradient(135deg, #636e72, #74b9ff) !important;
}

.stat-item {
  text-align: center;
  padding: 1rem;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05));
  margin-bottom: 0.75rem;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
}

.stat-item:hover {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.1);
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: block;
}

.stat-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.5rem;
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 0.25rem;
}

.action-buttons .btn {
  min-width: 32px;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
  border: none;
  position: relative;
  overflow: hidden;
}

.action-buttons .btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s;
}

.action-buttons .btn:hover::before {
  left: 100%;
}

.action-buttons .btn:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.action-buttons .btn-warning {
  background: linear-gradient(135deg, #fdcb6e, #e17055);
  color: white;
  box-shadow: 0 4px 15px rgba(253, 203, 110, 0.3);
}

.action-buttons .btn-warning:hover {
  box-shadow: 0 8px 25px rgba(253, 203, 110, 0.4);
}

.action-buttons .btn-danger {
  background: linear-gradient(135deg, #e17055, #d63031);
  color: white;
  box-shadow: 0 4px 15px rgba(225, 112, 85, 0.3);
}

.action-buttons .btn-danger:hover {
  box-shadow: 0 8px 25px rgba(225, 112, 85, 0.4);
}

.btn-outline-primary.btn-sm {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border: 2px solid rgba(102, 126, 234, 0.3);
  border-radius: 12px;
  padding: 0.5rem 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-outline-primary.btn-sm:hover {
  background: rgba(102, 126, 234, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
}

/* 加载、错误和空状态样式 */
.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.05);
  border: 1px solid #E0F2FE;
  margin: 2rem 0;
}

.loading-state {
  color: #0EA5E9;
}

.loading-state .spinner-border {
  width: 3rem;
  height: 3rem;
  margin-bottom: 1.5rem;
  border-color: #0EA5E9;
  border-right-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state h3 {
  color: #0C4A6E;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
}

.loading-state p {
  color: #475569;
  margin: 0;
}

.error-state {
  color: #DC2626;
}

.error-state i {
  font-size: 3rem;
  margin-bottom: 1.5rem;
  color: #EF4444;
}

.error-state h3 {
  color: #991B1B;
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.error-state p {
  color: #475569;
  margin-bottom: 2rem;
}

.empty-state {
  color: #64748B;
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: #BAE6FD;
}

.empty-state h3 {
  color: #0C4A6E;
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.empty-state p {
  color: #475569;
  margin-bottom: 2rem;
}

@media (max-width: 768px) {
  .question-bank-list {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem;
  }
  
  .filters-section {
    padding: 1.25rem;
  }
  
  .bank-grid {
    grid-template-columns: 1fr;
  }
  
  .loading-state,
  .error-state,
  .empty-state {
    padding: 2rem 1rem;
  }
}
</style>